import React from "react";
import { ResponsiveContainer, ComposedChart, Line, Area, CartesianGrid, XAxis, YAxis } from "recharts";
import { motion } from "framer-motion";

const SparklineChart = ({ data = [], isPositive, ticker }) => {

  if (!Array.isArray(data) || data.length === 0) {
    return <div style={{ width: "80px", height: "35px", background: "#f3f4f6" }} />;
  }

  // Generate a unique gradient ID per chart
  const gradientId = `colorFill-${ticker}`;

  // Get current time in ET (New York)
  let now = new Date();

  // ✅ 9:30 AM ET in UTC (Eastern Time is UTC -5 or UTC -4 during DST)
  const marketOpen = new Date();
  marketOpen.setUTCHours(12, 30, 0, 0); // 9:30 AM ET -> 13:30 UTC (Standard), 12:30 UTC (DST)
  
  // ✅ 4:00 PM ET in UTC
  const marketClose = new Date();
  marketClose.setUTCHours(20, 0, 0, 0); // 4:00 PM ET -> 20:00 UTC (Standard), 19:00 UTC (DST)

  if (now < marketOpen  || now > marketClose) {
    marketOpen.setUTCDate(marketOpen.getUTCDate() - 1);
    marketClose.setUTCDate(marketClose.getUTCDate() - 1);
    now = new Date(marketClose); // Set now to yesterday's market close
  }

  const elapsedTime = now.getTime() - marketOpen.getTime();
  const totalMarketTime = marketClose.getTime() - marketOpen.getTime();

  // console.log("Adjusted Market Open:");
  // console.log("Adjusted Market Close:");
  // console.log("Elapsed Time:", elapsedTime);
  // console.log("Total Market Time:", elapsedTime/totalMarketTime);


  // Calculate how much of the chart should be filled based on time progress
  let progress = Math.max(0, Math.min(1, elapsedTime / totalMarketTime));

  // Ensure chart fills dynamically based on time
  const visibleHistoryLength = Math.ceil(data.length * progress);
  const visibleHistory = data.slice(0, visibleHistoryLength);

  // Calculate the average price of the stock today
  const totalSum = data.reduce((sum, price) => sum + price, 0);
  const avgPrice = data.length > 0 ? totalSum / data.length : 0;

  // Prepare data for Recharts
  const chartData = data.map((price, index) => ({
    index,
    price: index < visibleHistoryLength ? price : null, // Show price up to the current time
    avg: avgPrice, // Gray average line (stays full-width)
  }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <ResponsiveContainer width={80} height={35}>
        <ComposedChart data={chartData}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={isPositive ? "#16a34a" : "#dc2626"} stopOpacity={0.4} />
              <stop offset="100%" stopColor={isPositive ? "#16a34a" : "#dc2626"} stopOpacity={0} />
            </linearGradient>
          </defs>

          {/* Dashed Gray Average Line (Full-width) */}
          <Line
            type="monotone"
            dataKey="avg"
            stroke={isPositive ? "#16a34a" : "#dc2626"}
            strokeWidth={1}
            strokeDasharray="3 2"
            dot={false}
          />

          {/* Main Stock Trend Line (Stops at current time) */}
          <Area
            type="monotone"
            dataKey="price"
            stroke={isPositive ? "#16a34a" : "#dc2626"}
            fill={`url(#${gradientId})`}
            strokeWidth={1.5}
            dot={false}
          />

        </ComposedChart>
      </ResponsiveContainer>
    </motion.div>
  );
};

export default SparklineChart;
